'use client';

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  ActionQueueContext,
  type ActionQueueContextValue,
  type ActionQueueItem,
  type ActionStep,
  type ActionQueueState,
  type ExecuteOptions,
} from '@/hooks/use-transactions';

// Per-step delay for the simulated tx queue. The real
// `TransactionQueueProvider` paces each step around an actual RPC
// round-trip (~1–3 s), but the tutorial brief is "click Take Over,
// see the slab crumble within 200 ms". 80 ms × N steps gives the
// queue snackbar enough time to flicker through `approve →
// buyout → success` while keeping click-to-animation under 200 ms
// even on the worst-case 2-step path.
const SIMULATED_STEP_MS = 80;

/** Initial empty state mirrored from `TransactionQueueProvider`. */
function emptyState(): ActionQueueState {
  return {
    isOpen: false,
    title: '',
    steps: [],
    isBatched: false,
    batchedCount: 0,
    totalBatchChunks: 0,
    isRetrying: false,
  };
}

function delay(ms: number): Promise<void> {
  return new Promise((res) => setTimeout(res, ms));
}

interface TutorialTransactionQueueProviderProps {
  children: ReactNode;
  /** Hook fired after every successful queue. The tutorial wires this to
   *  drive scripted state mutations (mark seat owned, advance step, etc.). */
  onQueueComplete?: (title: string, results: Map<string, unknown>) => void;
}

/**
 * Drop-in replacement for `TransactionQueueProvider` used during the tutorial.
 *
 * `executeActions` walks the supplied steps with a small delay each so the
 * snackbar/queue UI animates exactly the way it does for real transactions,
 * but no wallet/RPC calls are made. The simulated `results` Map is populated
 * with `null` per step so consumer `onSuccess` callbacks (which only inspect
 * map presence today) keep working unchanged.
 */
export function TutorialTransactionQueueProvider({
  children,
  onQueueComplete,
}: TutorialTransactionQueueProviderProps) {
  const [state, setState] = useState<ActionQueueState>(emptyState);
  const lastActionsRef = useRef<ActionQueueItem[]>([]);
  const lastTitleRef = useRef<string>('');
  const lastOptionsRef = useRef<ExecuteOptions | undefined>(undefined);

  const updateStep = useCallback((id: string, updates: Partial<ActionStep>) => {
    setState((prev) => ({
      ...prev,
      steps: prev.steps.map((s) => (s.id === id ? { ...s, ...updates } : s)),
    }));
  }, []);

  const close = useCallback(() => {
    setState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const cancelQueue = useCallback(() => {
    setState((prev) => ({
      ...prev,
      steps: prev.steps.map((s) =>
        s.status === 'waiting' || s.status === 'pending'
          ? { ...s, status: 'cancelled' }
          : s,
      ),
      isOpen: false,
    }));
  }, []);

  const runQueue = useCallback(
    async (
      title: string,
      actions: ActionQueueItem[],
      options?: ExecuteOptions,
    ): Promise<{ success: boolean; results: Map<string, unknown> }> => {
      lastActionsRef.current = actions;
      lastTitleRef.current = title;
      lastOptionsRef.current = options;

      const initialSteps: ActionStep[] = actions.map((a) => ({
        id: a.id,
        label: a.label,
        description: a.description,
        status: 'waiting',
      }));

      setState({
        isOpen: true,
        title,
        steps: initialSteps,
        isBatched: false,
        batchedCount: 0,
        totalBatchChunks: 0,
        isRetrying: false,
      });

      const results = new Map<string, unknown>();
      try {
        for (let i = 0; i < actions.length; i++) {
          const action = actions[i];
          setState((prev) => ({
            ...prev,
            steps: prev.steps.map((s, idx) =>
              idx === i ? { ...s, status: 'pending' } : s,
            ),
          }));

          await delay(SIMULATED_STEP_MS);

          // Simulated "tx hash" so the snackbar's "view tx" affordance still
          // renders something — it's a plain string and never followed in
          // tutorial mode.
          const fakeHash = (`0xtut${i.toString(16).padStart(60, '0')}`) as `0x${string}`;

          setState((prev) => ({
            ...prev,
            steps: prev.steps.map((s, idx) =>
              idx === i ? { ...s, status: 'success', hash: fakeHash } : s,
            ),
          }));

          results.set(action.id, null);
          options?.onStepComplete?.(action.id, null);
        }

        options?.onSuccess?.(results);
        onQueueComplete?.(title, results);
        // Auto-close shortly after success. 400 ms is just long
        // enough for the snackbar's success-state to register
        // visually without lingering on screen during the buyout
        // animation that's already firing — the real
        // `TransactionQueueProvider` waits 1.2 s, but in tutorial
        // mode the dialogue + crumble + camera follow are already
        // doing the heavy "the buyout completed" lifting.
        setTimeout(() => {
          setState((prev) => ({ ...prev, isOpen: false }));
        }, 400);
        return { success: true, results };
      } catch (error) {
        const err = error as Error;
        options?.onError?.(err, actions[0]?.id ?? 'unknown');
        return { success: false, results };
      }
    },
    [onQueueComplete],
  );

  const retryFromFailed = useCallback(async () => {
    return runQueue(
      lastTitleRef.current,
      lastActionsRef.current,
      lastOptionsRef.current,
    );
  }, [runQueue]);

  const retryAll = retryFromFailed;

  const value = useMemo<ActionQueueContextValue>(
    () => ({
      state,
      supportsBatching: false,
      executeActions: runQueue,
      retryFromFailed,
      retryAll,
      cancelQueue,
      canRetry: state.steps.some((s) => s.status === 'error' || s.status === 'cancelled'),
      close,
      updateStep,
      // Tutorial runs against synthetic state with no real wallet; the
      // provider warmer is a no-op here.
      prefetchProvider: () => {},
    }),
    [state, runQueue, retryFromFailed, retryAll, cancelQueue, close, updateStep],
  );

  return (
    <ActionQueueContext.Provider value={value}>
      {children}
    </ActionQueueContext.Provider>
  );
}
